'use client';

/**
 * SponsorshipsTab — sponsored cohorts (a foundation funds the subscription for
 * a group of organizations).
 *
 *   • Agreements: sponsor, the organization record its invoices go to, start
 *     date, cohort discount and the funding schedule by contract year
 *   • Members: the organizations in the cohort
 *   • This year: each member's sponsor share and the cohort discount; members'
 *     own invoices show both as credit lines automatically
 *   • Charges and the cohort invoice: one itemized invoice to the organization the
 *     cohort is billed to (the cohort lead or another designated org, not
 *     necessarily the funder) for
 *     its unbilled shares
 *   • Dashboard: adoption and outcomes per organization — counts only
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FileText, HeartHandshake, Loader2, Plus, UserMinus } from 'lucide-react';

const PLATFORM_BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) ||
  'https://weavein-api.onrender.com';

function headers(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  if (cboId) h['X-CBO-ID'] = cboId;
  if (tenantSlug) h['X-Tenant-ID'] = tenantSlug;
  return h;
}
async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${path}`, { ...init, headers: { ...headers(), ...(init.headers || {}) } });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
  return data as T;
}

const money = (cents: number) => `$${(Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
interface Org { id: number; name: string }

export default function SponsorshipsTab() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [list, setList] = useState<any[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [charges, setCharges] = useState<any[]>([]);
  const [dash, setDash] = useState<any>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [addOrg, setAddOrg] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '', sponsor_name: '', sponsor_cbo_id: '', start_date: new Date().toISOString().slice(0, 10),
    discount_mode: 'cohort_steps', discount_percent: '', y1: '100', y2: '100', y3: '50',
  });

  useEffect(() => {
    fetch(`${PLATFORM_BACKEND_URL}/api/cbos`, { headers: headers() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setOrgs((Array.isArray(d) ? d : d.cbos || []).map((o: any) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
  }, []);

  const loadList = useCallback(async () => {
    try {
      const r = await api<{ sponsorships: any[] }>('/sponsorships');
      setList(r.sponsorships);
      setSelected((cur) => cur || r.sponsorships[0]?.id || null);
    } catch (e: any) { setError(e.message); }
  }, []);
  const loadDetail = useCallback(async () => {
    if (!selected) return;
    try {
      const [d, c, db] = await Promise.all([
        api(`/sponsorships/${selected}`), api<{ charges: any[] }>(`/sponsorships/${selected}/charges`), api(`/sponsorships/${selected}/dashboard`),
      ]);
      setDetail(d); setCharges(c.charges); setDash(db);
    } catch (e: any) { setError(e.message); }
  }, [selected]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const run = async (key: string, fn: () => Promise<any>, done: (r: any) => string) => {
    setBusy(key); setError(null); setNotice(null);
    try { const r = await fn(); setNotice(done(r)); await loadList(); await loadDetail(); }
    catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };

  const create = () => run('create', async () => {
    const r = await api<{ id: string }>('/sponsorships', {
      method: 'POST',
      body: JSON.stringify({
        name: form.name, sponsor_name: form.sponsor_name,
        billed_to_cbo_id: form.sponsor_cbo_id ? Number(form.sponsor_cbo_id) : null,
        start_date: form.start_date, discount_mode: form.discount_mode,
        discount_percent: form.discount_mode === 'fixed' ? Number(form.discount_percent) : null,
        funding_schedule: [
          { year: 1, sponsor_percent: Number(form.y1) }, { year: 2, sponsor_percent: Number(form.y2) }, { year: 3, sponsor_percent: Number(form.y3) },
        ],
      }),
    });
    setSelected(r.id); setShowCreate(false);
    return r;
  }, () => 'Created the sponsorship. Add the cohort’s organizations next.');

  const unbilled = charges.filter((c) => !c.sponsor_invoice_id);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><HeartHandshake className="h-5 w-5" /> Sponsored cohorts</h2>
          <p className="text-sm text-gray-500 max-w-3xl">
            A funder covers the subscription for a group of organizations. Each organization’s invoice shows the cohort discount and the
            funded share; one itemized invoice goes to the organization the cohort is billed to (e.g. the cohort lead). Usage beyond allowances stays with each organization.
          </p>
        </div>
        <button onClick={() => setShowCreate((v) => !v)} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700">
          <Plus className="h-4 w-4" /> New sponsorship
        </button>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {notice && !error && <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</div>}

      {showCreate && (
        <section className="rounded-lg border border-gray-200 bg-white p-4 space-y-3 dark:bg-gray-800 dark:border-gray-700">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
            <label><span className="block text-xs text-gray-500 mb-1">Agreement name</span>
              <input id="sp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sobrato 2026 cohort" className="w-full rounded border px-2 py-1.5" /></label>
            <label><span className="block text-xs text-gray-500 mb-1">Funded by</span>
              <input id="sp-sponsor" value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })} placeholder="Sobrato Philanthropies" className="w-full rounded border px-2 py-1.5" /></label>
            <label><span className="block text-xs text-gray-500 mb-1">Billed to (cohort lead or designated organization)</span>
              <select id="sp-sponsor-org" value={form.sponsor_cbo_id} onChange={(e) => setForm({ ...form, sponsor_cbo_id: e.target.value })} className="w-full rounded border px-2 py-1.5">
                <option value="">Set later…</option>{orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select></label>
            <label><span className="block text-xs text-gray-500 mb-1">Start date</span>
              <input id="sp-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full rounded border px-2 py-1.5" /></label>
            <label><span className="block text-xs text-gray-500 mb-1">Cohort discount</span>
              <select id="sp-discount-mode" value={form.discount_mode} onChange={(e) => setForm({ ...form, discount_mode: e.target.value })} className="w-full rounded border px-2 py-1.5">
                <option value="cohort_steps">Price book steps (15% / 25% / 30% by cohort size)</option>
                <option value="fixed">Fixed percent</option>
                <option value="none">None</option>
              </select></label>
            {form.discount_mode === 'fixed' && (
              <label><span className="block text-xs text-gray-500 mb-1">Discount %</span>
                <input id="sp-discount" type="number" min="0" max="100" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="w-full rounded border px-2 py-1.5" /></label>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3 text-sm">
            <span className="text-xs text-gray-500">Sponsor pays (% of subscription):</span>
            {(['y1', 'y2', 'y3'] as const).map((k, i) => (
              <label key={k}><span className="block text-xs text-gray-500 mb-1">Year {i + 1}</span>
                <input id={`sp-${k}`} type="number" min="0" max="100" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-20 rounded border px-2 py-1" /></label>
            ))}
            <span className="text-xs text-gray-500">Year 4 on: the organization pays, keeping the cohort discount.</span>
          </div>
          <button onClick={create} disabled={!!busy || !form.name || !form.sponsor_name} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white disabled:bg-gray-400">
            {busy === 'create' ? 'Creating…' : 'Create sponsorship'}
          </button>
        </section>
      )}

      {list.length === 0 ? (
        <p className="text-sm text-gray-500">No sponsorships yet.</p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <div className="space-y-1">
            {list.map((a) => (
              <button key={a.id} onClick={() => setSelected(a.id)} className={`w-full rounded-md border px-3 py-2 text-left text-sm ${selected === a.id ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                <div className="font-medium truncate">{a.name}</div>
                <div className="text-xs text-gray-500">{a.sponsor_name} · {a.active_orgs} orgs · {money(a.unbilled_cents)} unbilled</div>
              </button>
            ))}
          </div>

          {detail && (
            <div className="min-w-0 space-y-4">
              <section className="rounded-lg border border-gray-200 bg-white p-4 dark:bg-gray-800 dark:border-gray-700">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">{detail.name}</div>
                    <div className="text-xs text-gray-500">
                      {detail.sponsor_name}{detail.sponsor_org_name ? ` · billed to ${detail.sponsor_org_name}` : ' · no billed-to organization yet'} · from {detail.start_date}
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <div>Contract year <span className="font-semibold">{detail.today.contract_year}</span>: sponsor pays <span className="font-semibold">{detail.today.sponsor_percent}%</span></div>
                    <div className="text-xs text-gray-500">Cohort discount {detail.today.cohort_discount_percent}% ({detail.today.active_orgs} organizations)</div>
                  </div>
                </div>
              </section>

              {dash && (
                <section className="rounded-lg border border-gray-200 bg-white p-4 dark:bg-gray-800 dark:border-gray-700 space-y-3">
                  <div className="flex flex-wrap gap-3 text-sm">
                    {[
                      ['Organizations', dash.totals.organizations], ['Seats', dash.totals.seats], ['Active members', dash.totals.active_members],
                      ['Outcomes (3 mo)', dash.totals.outcomes_90d], ['Funded to date', `$${dash.totals.sponsored_to_date.toLocaleString()}`],
                    ].map(([l, v]) => (
                      <div key={String(l)} className="rounded bg-gray-50 px-3 py-2 dark:bg-gray-900"><div className="text-xs text-gray-500">{l}</div><div className="text-lg font-semibold tabular-nums">{v}</div></div>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <select id="sp-add-org" value={addOrg} onChange={(e) => setAddOrg(e.target.value)} className="rounded border px-2 py-1.5">
                      <option value="">Add an organization…</option>
                      {orgs.filter((o) => !dash.organizations.some((m: any) => m.cbo_id === o.id && !m.left_at)).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <button disabled={!addOrg || !!busy} onClick={() => run('add', () => api(`/sponsorships/${selected}/members`, { method: 'POST', body: JSON.stringify({ cbo_id: Number(addOrg) }) }), () => { setAddOrg(''); return 'Added to the cohort.'; })}
                      className="inline-flex items-center gap-1 rounded bg-gray-100 px-2.5 py-1.5 hover:bg-gray-200 disabled:opacity-50"><Plus className="h-4 w-4" /> Add</button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead className="text-xs text-gray-500 border-b"><tr>
                        <th className="py-1.5 text-left">Organization</th><th className="text-left">Plan</th><th className="text-right">Seats</th>
                        <th className="text-right">Active members</th><th className="text-right">Outcomes (3 mo)</th><th className="text-right">Sponsored</th><th />
                      </tr></thead>
                      <tbody className="divide-y">
                        {dash.organizations.map((o: any) => (
                          <tr key={o.cbo_id} className={o.left_at ? 'text-gray-400' : ''}>
                            <td className="py-1.5">{o.org_name}{o.left_at ? ` (left ${o.left_at})` : ''}</td>
                            <td>{o.plan || '—'}</td><td className="text-right tabular-nums">{o.seats}</td>
                            <td className="text-right tabular-nums">{o.active_members}</td><td className="text-right tabular-nums">{o.outcomes_90d}</td>
                            <td className="text-right tabular-nums">{money(o.sponsored_cents)}</td>
                            <td className="text-right">{!o.left_at && (
                              <button onClick={() => run(`rm:${o.cbo_id}`, () => api(`/sponsorships/${selected}/members/${o.cbo_id}`, { method: 'DELETE' }), () => 'Removed from the cohort from today.')}
                                className="text-gray-400 hover:text-red-600" title="Leaves the cohort today"><UserMinus className="h-4 w-4" /></button>
                            )}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500">Counts only: no member names or records are shared with the sponsor.</p>
                </section>
              )}

              <section className="rounded-lg border border-gray-200 bg-white p-4 dark:bg-gray-800 dark:border-gray-700 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">Cohort funding share of member invoices</h3>
                  <button
                    disabled={!unbilled.length || !!busy}
                    onClick={() => run('invoice', () => api(`/sponsorships/${selected}/generate-invoice`, { method: 'POST', body: JSON.stringify({}) }), (r) => `Draft invoice ${r.invoice_number} to ${detail.sponsor_org_name || 'the billed-to organization'}: ${r.lines} line(s), $${r.total.toLocaleString()}. Review and send it in Billing → Invoices.`)}
                    className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:bg-gray-400"
                  >
                    {busy === 'invoice' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Invoice {detail.sponsor_org_name || 'the cohort payer'} ({money(unbilled.reduce((a, c) => a + Number(c.amount_cents), 0))})
                  </button>
                </div>
                {charges.length === 0 ? (
                  <p className="text-sm text-gray-500">Shares appear here as member organizations are invoiced.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-sm">
                      <thead className="text-xs text-gray-500 border-b"><tr>
                        <th className="py-1.5 text-left">Organization</th><th className="text-left">Period</th><th className="text-left">Org invoice</th>
                        <th className="text-right">Year · share</th><th className="text-right">Amount</th><th className="text-left pl-3">Billed to sponsor</th>
                      </tr></thead>
                      <tbody className="divide-y">
                        {charges.map((c) => (
                          <tr key={c.id}>
                            <td className="py-1.5">{c.org_name}</td><td>{c.period_start} – {c.period_end}</td><td>{c.invoice_number || c.org_invoice_id}</td>
                            <td className="text-right">Y{c.contract_year} · {c.sponsor_percent}%</td><td className="text-right tabular-nums">{money(c.amount_cents)}</td>
                            <td className="pl-3">{c.sponsor_invoice_id ? 'Yes' : <span className="text-amber-700">Not yet</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
